import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row } from "../../common/components/Grid";
import ReactForm from "../../common/components/ReactForm";
import Select from "../../common/components/Select";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { canCreateNotificationRule, canRemoveNotificationRule, createNotificationRule, removeNotificationRule, useNotificationRules } from "../../reducers/notificationRuleSlice";
import ContactListDropdown from "../ContactListDropdown";
import GroupedSettings from "./GroupedSettings";
import PropTypes from "prop-types";

const defaultSort = ['list'];

const newRuleTemplate = {};

const NotificationRuleDetails = forwardRef(({ value: rule, groupCtrl, disabled = false, readonly = false, onChange }, formRef) => {
    const { t } = useTranslation();
    return <>
        <ReactForm ref={formRef}>
            <Row>
                <Col md>
                    <ContactListDropdown value={rule.list} placeholder={t('contactList', { count: 1 })} required disabled={disabled} readonly={readonly} onChange={onChange('list')}></ContactListDropdown>
                </Col>
                <Col md>
                    <Select value={rule.event} placeholder={t('notificationEvent')} options={["Job Created", "Job Closed"]} required disabled={disabled} readonly={readonly} onChange={onChange('event')}></Select>
                </Col>
            </Row>
        </ReactForm>
        {groupCtrl}
    </>
})

export const NotificationRules = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const clientConfig = useClientConfig();
    const network = useNetwork();
    const rules = useNotificationRules();
    const sortFields = useMemo(() => ({
        list: t('list'),
        event: t('event')
    }), [t]);
    const groupingCtrls = useMemo(() => {
        const ctrls = [{ type: 'type', id: 'type', label: t('type', { count: 1 }), title: t('type', { count: 1 }), field: ['site','level','type'], sortfield: 'type', strict: false, wildcard: t('anyWildcard'), uuid: '35bedf50-8177-4601-95b4-7839bd475e41' }]
        if (clientConfig.enableLevels) {
            ctrls.unshift({ type: 'level', id: 'level', label: t('level', { count: 1 }), title: t('level', { count: 1 }), field: ['site','level','type'], sortfield: 'level', strict: false, wildcard: t('anyWildcard'), uuid: 'ee5ec25c-cd0c-4ad4-8082-dd0d9d578e21' });
        }
        if (clientConfig.enableSites) {
            ctrls.unshift({ type: 'site', id: 'site', label: t('site', { count: 1 }), title: t('site', { count: 1 }), field: ['site','level','type'], sortfield: 'site', strict: false, wildcard: t('anyWildcard'), uuid: '76669c45-2267-490b-90c1-c1e63b59d5f4' });
        }
        ctrls.push({ type: 'problem', id: 'problem', label: t('problem', { count: 1 }), title: t('problem', { count: 1 }), field: ['site','level','type','problem'], sortfield: 'problem', wildcard: t('anyWildcard'), uuid: '6d4cc8e4-3f57-4f60-8a9d-8b6e2aba64a5' });
        ctrls.push({ type: 'priority', id: 'priority', label: t('priority', { count: 1 }), title: t('priority', { count: 1 }), field: 'priority', sortfield: 'priority', wildcard: t('anyWildcard'), uuid: '72da3348-e853-4c10-9c0a-b3f5469e2d0f' });
        ctrls.push({ type: 'zone', id: 'zone', label: t('zone', { count: 1 }), title: t('zone', { count: 1 }), field: 'zone', sortField: 'zone', wildcard: t('noneWildcard'), uuid: 'dfa2692f-22e9-4c71-be8e-0af07e1678db' });
        return ctrls;
    }, [clientConfig.enableLevels, clientConfig.enableSites, t]);
    const canAdd = entry => (!entry.id || (!!entry.list && !!entry.event)) && canCreateNotificationRule(credentials);
    const canRemove = entry => canRemoveNotificationRule(credentials, entry);
    const onAdd = useCallback(rule => {
        return createNotificationRule(network, rule)
    }, [network]);
    const onRemove = useCallback(rule => removeNotificationRule(network, rule), [network]);

    return <GroupedSettings Details={NotificationRuleDetails} data={rules} uuid={uuid} title={label} pkey="id"
            defaultEntry={newRuleTemplate} defaultSort={defaultSort} sortFields={sortFields}
            onClose={onClose} makeLabel={e => <Row>
                <Col xs>
                    <ContactListDropdown readonly={true} value={e.list} hideLabel={true}></ContactListDropdown>
                </Col>
                <Col>
                    {e.event}
                </Col>
            </Row>}
            initialGroupingField="list"
            groupingExclusions={['site','level','type','priority','problem','zone','id']}
            canAdd={canAdd} canRemove={canRemove} canUpdate={() => false}
            onAdd={onAdd} onRemove={onRemove}
            groupingCtrls={groupingCtrls}></GroupedSettings>
}
NotificationRules.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default NotificationRules;